import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";
import { IMAGES } from "@/lib/content";

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
    const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
    const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section
            ref={ref}
            id="thesis"
            className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-20"
            data-testid="hero"
        >
            {/* Parallax background */}
            <motion.div
                style={{ y: bgY, scale: bgScale }}
                className="pointer-events-none absolute inset-0 z-0"
            >
                <img
                    src={IMAGES.goldBg}
                    alt=""
                    className="h-full w-full object-cover opacity-[0.16] sepia-[0.3]"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-navy-950/70 via-navy-950/85 to-navy-950" />
                <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-transparent to-navy-950/60" />
            </motion.div>

            <motion.div
                style={{ y: contentY, opacity: contentOpacity }}
                className="relative z-10 mx-auto w-full max-w-7xl px-6 md:px-12"
            >
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1, duration: 0.8 }}
                    className="mb-8 flex items-center gap-4"
                >
                    <span className="h-px w-10 bg-gold/60" />
                    <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">
                        Investment Thesis
                    </span>
                </motion.div>

                <MaskedLines
                    lines={[
                        "Most people know they should",
                        "plan for what happens after",
                        "they're gone.",
                    ]}
                    className="font-display text-4xl sm:text-5xl lg:text-7xl font-light leading-[1.05] tracking-tight text-white"
                    delay={0.15}
                />

                <MaskedLines
                    lines={["Almost no one does it."]}
                    className="mt-4 font-display text-4xl sm:text-5xl lg:text-7xl font-light italic leading-[1.05] tracking-tight text-gold"
                    delay={0.7}
                />

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.1, duration: 0.9 }}
                    className="mt-10 max-w-2xl text-base md:text-xl leading-relaxed text-white/70"
                >
                    HEIR turns the most avoided conversation of a lifetime into a
                    simple{" "}
                    <span className="font-medium text-white">
                        $88 starting point
                    </span>, and builds a multi-generational relationship from there.
                </motion.p>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
            >
                <ArrowDown className="h-5 w-5 animate-bounce text-white/40" />
            </motion.div>
        </section>
    );
}
